import React, { useEffect, useState } from 'react'
import { CircularProgress, Container, makeStyles } from '@material-ui/core'
import { organizationStatus, SubjectStatus } from 'constants/index'
import { Box } from '@mui/material'
import PageNotFound from 'components/PageNotFound'
import ThankYou from 'components/ThankYou'
import { toast } from 'utils/notify'
import moment from 'moment'
import { getStatusBasedSubTitle } from 'utils'
import LogoWithConference from 'components/LogoWithConference'
import { cloudFunctionsInstance } from 'config/axios'
import useValidateSpeakerToken from 'hooks/useValidateSpeakerToken'
import Speaker from 'views/admin/people/components/Speaker'

const SpeakerProfile = () => {
  const classes = useStyles()
  const { isLoading, isTokenValid, speaker, conference, refetch } = useValidateSpeakerToken()

  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    if (
      (!isFormSubmitted && speaker?.status === organizationStatus.AWAITING_RESPONSE) ||
      (speaker?.updateInfoNotificationSent && !speaker?.submittedAt)
    ) {
      setShowForm(true)
    }
  }, [speaker?.status])

  const handleEditSubmittedForm = async () => {
    setIsFormSubmitted(false)
    setShowForm(true)
    refetch && refetch()
  }

  const handleSubmit = async (values) => {
    const nowDateTime = moment().utc().valueOf()
    const payload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      fullName: values.fullName.trim(),
      email: values.email.trim(),

      credential: values?.credential.trim() ?? '',
      position: values.position.trim() ?? '',
      company: values.company.trim() ?? '',
      phone: values?.phone.trim() ?? '',
      city: values?.city.trim() ?? '',
      state: values?.state.trim() ?? '',
      country: values?.country.trim() ?? '',

      websiteUrl: values.websiteUrl.trim(),
      bio: values.bio.trim(),
      originalBio: values.originalBio?.trim() ?? '',

      status: SubjectStatus.PENDING_DATA_REVIEW,
      submittedAt: nowDateTime,
      profileImageDeleted: values.profileImageDeleted
    }

    try {
      const form = new FormData()

      form.append('speakerId', speaker?.id)
      form.append('conferenceId', conference?.id)
      form.append('payloadData', JSON.stringify(payload))

      if (values.profileImage) {
        form.append('profileImage', values.profileImage)
      }
      await cloudFunctionsInstance.post('/submitSpeakerProfile', form)
      setIsFormSubmitted(true)
      setShowForm(false)
      toast.success('Data submitted successfully.')
    } catch (err) {
      console.error(err)
      toast.error(err?.response?.data?.message || 'Form submission failed. Please try again.')
    }
  }

  if (isLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          justifyContent: 'center',
          mt: '5rem'
        }}>
        <CircularProgress />
        Loading, please wait...
      </Box>
    )
  else if (!isTokenValid) return <PageNotFound />
  else if (isFormSubmitted && !showForm)
    return (
      <ThankYou
        onAction={handleEditSubmittedForm}
        subTitle="Thank you for submitting the requested form. We appreciate your cooperation. If you want to make any changes, please click the button below."
      />
    )
  else if (
    !showForm &&
    (speaker?.status === SubjectStatus.APPROVED ||
      speaker?.status === SubjectStatus.PENDING_DATA_REVIEW)
  )
    return (
      <ThankYou
        onAction={handleEditSubmittedForm}
        subTitle={getStatusBasedSubTitle(speaker?.status)}
      />
    )
  else if (!isFormSubmitted && showForm)
    return (
      <Box className={classes.pageScroll}>
        <Container maxWidth="xl" className={classes.container}>
          <LogoWithConference confName={conference?.confName} />
          <Speaker
            speaker={speaker}
            submitTitle="Submit Your Information"
            handleSubmit={handleSubmit}
          />
        </Container>
      </Box>
    )
  else return null
}
export default SpeakerProfile

const useStyles = makeStyles({
  pageScroll: {
    height: '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: 6
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#cbd5e1',
      borderRadius: 6
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#94a3b8'
    }
  },
  container: {
    paddingBottom: '1.5rem',
    paddingTop: '1.5rem'
  }
})
